import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiUrl } from '../../services/api';
import { useAuth } from '../../store/AuthContext';
import { Radar, Save, Loader2 } from 'lucide-react';

const ScoutParamsPage = () => {
  const { token } = useAuth();
  const [scoutScanRadius, setScoutScanRadius] = useState('500');
  const [scoutSafeArea, setScoutSafeArea] = useState('300');
  const [fetching, setFetching] = useState(true);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const resp = await axios.get(`${getApiUrl()}/setup`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const settings = resp.data.data || [];
        const radiusSetting = settings.find(s => s.key === 'scout_scan_radius');
        const safeAreaSetting = settings.find(s => s.key === 'scout_safe_area');
        if (radiusSetting) setScoutScanRadius(radiusSetting.value);
        if (safeAreaSetting) setScoutSafeArea(safeAreaSetting.value);
      } catch (err) {
        console.error('Failed to fetch scout params', err);
      } finally {
        setFetching(false);
      }
    };
    fetchSettings();
  }, [token]);

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);
    try {
      await Promise.all([
        axios.post(`${getApiUrl()}/setup`, { key: 'scout_scan_radius', value: scoutScanRadius }, { headers: { Authorization: `Bearer ${token}` } }),
        axios.post(`${getApiUrl()}/setup`, { key: 'scout_safe_area', value: scoutSafeArea }, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setMessage({ type: 'success', text: 'SCOUT PARAMETERS UPDATED SUCCESSFULLY' });
    } catch {
      setMessage({ type: 'error', text: 'FAILED TO UPDATE SCOUT PARAMETERS' });
    } finally {
      setLoading(false);
    }
  };

  if (fetching) return <div style={{ color: '#E27B58', padding: '20px' }}>LOADING SCOUT CONFIGURATION...</div>;

  return (
    <div style={{ maxWidth: '700px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '30px' }}>
        <Radar size={28} color="#E27B58" />
        <h1 style={{ margin: 0, letterSpacing: '2px', textTransform: 'uppercase' }}>Scout Params.</h1>
      </div>

      <div style={{
        background: 'rgba(5, 7, 10, 0.9)',
        border: '1px solid rgba(226, 123, 88, 0.4)',
        padding: '30px',
        borderRadius: '4px'
      }}>
        <form onSubmit={handleSave}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '40px', marginBottom: '30px' }}>

            {/* Scan Radius */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <label style={{ display: 'block', color: '#888', fontSize: '0.7rem', letterSpacing: '1px' }}>
                  MAX SCAN RADIUS (METERS)
                </label>
                <span style={{ color: '#E27B58', fontSize: '1.1rem', fontWeight: 'bold', fontFamily: 'monospace' }}>
                  {scoutScanRadius}m
                </span>
              </div>
              <input
                type="range"
                style={{ width: '100%', accentColor: '#E27B58', cursor: 'pointer' }}
                value={scoutScanRadius}
                onChange={(e) => setScoutScanRadius(e.target.value)}
                min="100"
                max="50000"
                step="50"
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.6rem', color: '#444', marginTop: '5px' }}>
                <span>100m</span>
                <span>25km</span>
                <span>50km</span>
              </div>
              <p style={{ fontSize: '0.65rem', color: '#555', marginTop: '10px', fontStyle: 'italic', lineHeight: '1.5' }}>
                Radio máximo al buscar competencia y motores en el análisis de viabilidad (Huff Scan).
              </p>
            </div>

            {/* Safe Area */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <label style={{ display: 'block', color: '#888', fontSize: '0.7rem', letterSpacing: '1px' }}>
                  MAX SAFE AREA (METERS)
                </label>
                <span style={{ color: '#E27B58', fontSize: '1.1rem', fontWeight: 'bold', fontFamily: 'monospace' }}>
                  {scoutSafeArea}m
                </span>
              </div>
              <input
                type="range"
                style={{ width: '100%', accentColor: '#E27B58', cursor: 'pointer' }}
                value={scoutSafeArea}
                onChange={(e) => setScoutSafeArea(e.target.value)}
                min="50"
                max="50000"
                step="50"
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.6rem', color: '#444', marginTop: '5px' }}>
                <span>50m</span>
                <span>25km</span>
                <span>50km</span>
              </div>
              <p style={{ fontSize: '0.65rem', color: '#555', marginTop: '10px', fontStyle: 'italic', lineHeight: '1.5' }}>
                Zona de exclusión alrededor de locales propios para evitar canibalización entre puntos de venta.
              </p>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="nasa-button"
            style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
          >
            {loading ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
            {loading ? 'SYNCHRONIZING...' : 'COMMIT SCOUT PARAMS'}
          </button>
        </form>

        {message && (
          <div style={{
            marginTop: '20px',
            padding: '12px',
            fontSize: '0.8rem',
            backgroundColor: message.type === 'success' ? 'rgba(0, 184, 148, 0.1)' : 'rgba(214, 48, 49, 0.1)',
            color: message.type === 'success' ? '#00B894' : '#D63031',
            border: `1px solid ${message.type === 'success' ? '#00B894' : '#D63031'}`,
            textAlign: 'center'
          }}>
            {message.text}
          </div>
        )}
      </div>
    </div>
  );
};

export default ScoutParamsPage;
